// Safe TSL Block DSL (STSL) — a declarative AST for PUBLIC user-authored blocks. There is NO host-language
// code: a block is a tree of whitelisted operations the controlled engine interprets (src/engine/tslBlock/
// interpret.ts). No eval / new Function / imports / network / fs / globals / loops / recursion / dynamic
// access — those are simply not representable in this grammar. Curated/internal blocks keep their reviewed
// TS/JS; only this DSL is allowed for user-published blocks.

import type { BlockCategory, CustomBlockSubtype, ParameterSchema } from '../../types/customBlockLibrary';

export const TSL_DSL_VERSION = 'tsl-block@1';

// ── Whitelisted vocabulary ────────────────────────────────────────────────────
/** OHLCV sources + derived price series (current bar). */
export const SOURCES = ['open', 'high', 'low', 'close', 'volume', 'hl2', 'hlc3', 'ohlc4'] as const;
export type Source = typeof SOURCES[number];

/** Sizing context fields (read-only) available to `qty` expressions. */
export const SIZING_CTX = ['equity', 'dayStartEquity', 'initialCapital', 'entryPrice', 'stopDistance', 'atr', 'price'] as const;
/** Risk context fields (read-only) available to `risk.action` expressions. */
export const RISK_CTX = ['barsSinceEntry', 'entryPrice', 'barClose', 'barHigh', 'barLow'] as const;

/** Causal indicators/transforms — implemented natively (deterministic, no-lookahead). args after source are
 *  literal-or-param periods. */
export const INDICATORS: Record<string, { arity: number; needsHL?: boolean }> = {
  sma: { arity: 2 }, ema: { arity: 2 }, rma: { arity: 2 }, wma: { arity: 2 },
  rsi: { arity: 2 }, stdev: { arity: 2 }, highest: { arity: 2 }, lowest: { arity: 2 },
  change: { arity: 2 }, roc: { arity: 2 }, atr: { arity: 1, needsHL: true }, tr: { arity: 0, needsHL: true },
  // Deterministic seeded pseudo-random series in [0,1) (mulberry32). arg = seed (literal/param). Causal
  // (value at bar i depends only on seed + i), reproducible → passes the determinism gate. The canonical
  // "null model" primitive: random entry timing for benchmark baselines.
  rng: { arity: 1 },
  // Same-symbol higher-timeframe resample. htf(htfMinutes, period, kindCode) — buckets the close stream by
  // floor(timestamp / htfMinutes·60s), runs a sub-indicator (kindCode 0=sma 1=ema 2=dema 3=tema, period) on
  // the CLOSED-bucket closes, and returns the LAST CLOSED bucket's value per bar (Pine lookahead_off:
  // never the still-forming bucket). Causal + prefix-invariant (a closed bucket's value is final).
  htf: { arity: 3 },
  // htfema1(htfMinutes, period) — higher-timeframe EMA with Pine `ta.ema(close[1], n)` +
  // request.security(lookahead_off): timestamp-bucketed, SMA-seeded EMA over CLOSED-bucket closes, exposing
  // the value EXCLUDING the most-recently-closed bucket (close[1] shift). Distinct from htf (no shift,
  // first-value EMA seed). Causal + prefix-invariant.
  htfema1: { arity: 2 },
};

export const BINOPS = ['+', '-', '*', '/', 'min', 'max'] as const;
export const CMPOPS = ['>', '<', '>=', '<=', '==', '!='] as const;
// nz(x) — coalesce a non-finite (NaN/±Inf) value to 0 (Pine's nz). Used to seed momentum-style series at
// bar 0 (e.g. ta.change's first-bar na → 0) so a downstream ema matches the Pine reference exactly.
export const UNOPS = ['neg', 'not', 'abs', 'sign', 'nz'] as const;

// ── Expression nodes (each evaluates to a number or boolean PER BAR) ───────────
export type Expr =
  | { t: 'num'; v: number }
  | { t: 'bool'; v: boolean }
  | { t: 'param'; name: string }                                   // declared parameter
  | { t: 'src'; src: Source }                                      // current-bar OHLCV/derived
  | { t: 'ctx'; field: string }                                    // sizing/risk context field
  | { t: 'state'; name: string }                                   // bounded scalar state (previous value)
  | { t: 'indicator'; name: string; args: Expr[] }                // whitelisted causal indicator
  | { t: 'prev'; expr: Expr; barsAgo: number }                    // safe historical indexing (bounded, past only)
  | { t: 'unop'; op: typeof UNOPS[number]; a: Expr }
  | { t: 'binop'; op: typeof BINOPS[number]; a: Expr; b: Expr }
  | { t: 'cmp'; op: typeof CMPOPS[number]; a: Expr; b: Expr }
  | { t: 'logic'; op: 'and' | 'or'; a: Expr; b: Expr }
  | { t: 'cross'; dir: 'over' | 'under'; a: Expr; b: Expr }       // crossover / crossunder
  | { t: 'cond'; cond: Expr; then: Expr; else: Expr };            // ternary (NOT a loop)

// ── Block definition (declarative; category-specific body) ─────────────────────
export interface TslBlock {
  dsl: typeof TSL_DSL_VERSION;
  category: BlockCategory;
  blockType: CustomBlockSubtype;
  params: ParameterSchema;                       // declared parameters (bounds = optimizer space)
  state?: Record<string, { init: number; update: Expr }>; // bounded scalar state vars
  // indicators / filters:
  value?: Expr;                                  // per-bar numeric output
  signal?: Expr;                                 // per-bar boolean (filters / signal generators)
  // sizing:
  qty?: Expr;                                    // qty expression over SIZING_CTX
  // risk_rules: action code 0=null/allow-through, 1=block_entry, 2=flatten, 3=allow, 4=resize_qty
  risk?: { action: Expr };
}

/** Visit every Expr node (pre-order). Used by the validator + interpreter for budgets/compilation. */
export function walk(e: Expr | undefined, fn: (n: Expr) => void): void {
  if (!e) return;
  fn(e);
  switch (e.t) {
    case 'indicator': e.args.forEach((a) => walk(a, fn)); break;
    case 'prev': walk(e.expr, fn); break;
    case 'unop': walk(e.a, fn); break;
    case 'binop': case 'cmp': case 'logic': case 'cross': walk(e.a, fn); walk(e.b, fn); break;
    case 'cond': walk(e.cond, fn); walk(e.then, fn); walk(e.else, fn); break;
    default: break; // leaves: num/bool/param/src/ctx/state
  }
}

/** Every Expr body present on a block (for whole-block budgets). */
export function blockExprs(b: TslBlock): Expr[] {
  const out: Expr[] = [];
  if (b.value) out.push(b.value);
  if (b.signal) out.push(b.signal);
  if (b.qty) out.push(b.qty);
  if (b.risk) out.push(b.risk.action);
  if (b.state) for (const s of Object.values(b.state)) out.push(s.update);
  return out;
}
